import React, {
    CSSProperties,
    ClipboardEventHandler,
    FocusEventHandler,
    FormEvent,
    FormEventHandler,
    forwardRef,
    KeyboardEvent,
    KeyboardEventHandler,
    RefObject,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState
} from 'react';
import './TextField.less';
import './MultilineText.less';
import {stripHtml} from 'string-strip-html';
import DOMPurify from 'dompurify';
import usePrevious from "../hooks/usePrevious";

export type MultilineTextProps = {
    // Should this component get focus
    autoFocus?: boolean,

    // Additional class names to append
    className?: string,

    // The default value for the component
    value?: string,

    // Data to pass back
    data?: any,

    // Is the field disabled?
    disabled?: boolean,

    // Should the data
    encrypted?: boolean,

    // Is the component currently editable?
    isEditable?: boolean,

    // Key value
    id: string | number,

    // Function to fire when the input field is blurred
    onBlur?: Function,

    // Cancel edit
    onCancel?: Function,

    // Optional onChange event
    onChange?: Function,

    // Called when the ENTER button is pressed
    onEnter?: Function,

    // After pressing enter, should the contents be reset?
    resetOnEnter?: boolean,

    // Should contents be stripped of formatting?
    stripHtml?: boolean,

    // Additional styles for object
    styles?: CSSProperties,

    // Placeholder
    placeholder?: string,
}

export type MultilineTextRef = {
    focus: () => void,
    clear: () => void
}

type StrippedEventHandler = FocusEvent | FormEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>;

/**
 * @param {string} value
 * @returns {{__html: string}}
 */
const createMarkup = (value: string): { __html: string; } => {
    return {__html: DOMPurify.sanitize(value)};
};

const MultilineText = forwardRef<MultilineTextRef, MultilineTextProps>((props, ref) => {
    const contentRef: RefObject<any> = React.createRef();

    const [value, setValue] = useState(props.value ?? '');
    const [markup, setMarkup] = useState(createMarkup(props.value ?? ''));

    const prevValue = usePrevious(value);

    useEffect(() => {
        if ((props.value || '') !== (prevValue || '')) {
            const newMarkup = createMarkup(props.value || '');
            if (newMarkup.__html !== markup.__html) {
                setTimeout(() => setMarkup(newMarkup), 0);
            }
        }
    }, [markup.__html, prevValue, props.value]);

    /**
     * Check for autofocus. If set, select the contents.
     */
    useEffect(() => {
        if (props.autoFocus) {
            contentRef.current.focus();

            const cell = contentRef.current;
            let range: Range, selection: Selection;
            if (window.getSelection) {
                selection = window.getSelection();
                range = document.createRange();
                range.selectNodeContents(cell);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
    }, [props.autoFocus, props.id, contentRef]);

    useImperativeHandle(ref, () => {
        return {
            focus() {
                contentRef.current.focus();
            },
            clear() {
                contentRef.current.innerHTML = '';
            }
        }
    }, [contentRef]);

    /**
     * Strip HTML tags from the content in the editable div.
     *
     * @param {StrippedEventHandler} e
     * @param {string} [override] - use this text rather than e.target.value
     * @returns {string}
     */
    const getStrippedContents = useCallback((e?: StrippedEventHandler, override?: string): string => {
        // @ts-ignore
        if (!props.stripHtml && e.target) return e.target.innerHTML || e.target.innerText || '';
        // @ts-ignore
        else return override || e.target.innerHTML || '';
    }, [props.stripHtml]);

    /**
     * Strip html formatting and return a stripped string.
     *
     * @param {string} input
     * @param {string[]} ignoreTags
     * @returns {string}
     */
    const stripHTML = useCallback((input: string, ignoreTags?: string[]): string => {
        if (!input) return '';
        else {
            const firstPass = stripHtml(input, {
                stripTogetherWithTheirContents: ["script", "style", "xml", "class"],
                ignoreTags: ignoreTags ?? ['div', 'p', 'br', 'em', 'strong', 'b', 'i', 'strong', 'u', 'ul', 'ol', 'li', 'hr', 'img', 'a', 'h1', 'h2', 'h3', 'h4'],
                skipHtmlDecoding: true
            }).result ?? ''

            return firstPass
                .replaceAll(/(<[^>]+) class=".*?"/ig, '$1')
                .replaceAll(/(<[^>]+) style=".*?"/ig, '$1')
                .replaceAll(/(<[^>]+) style='.*?'/ig, '$1')
                .replaceAll(/(<[^>]+) href=".*?"/ig, '$1')
                .replaceAll(/(<[^>]+) href='.*?'/ig, '$1')
                .replaceAll(/·[\s?]+[&nbsp;]+/ig, '&nbsp;&nbsp;&bull; ')
                .replaceAll(/([0-9]+)\.[\s?]+[&nbsp;]+/g, '&nbsp;&nbsp;$1. ');
        }
    }, []);

    /**
     * We do the stripping in blur so as not to mess everything up
     *
     * @param e
     */
    const onBlur = useCallback<FocusEventHandler<HTMLDivElement>>(e => {
        setValue(e.target.innerHTML ?? '');

        if (props.onBlur) {
            props.onBlur(e, props.data, getStrippedContents(e));
        }
    }, [getStrippedContents, props]);

    const onChange = useCallback<FormEventHandler<HTMLDivElement>>(e => {
        if (typeof props.onChange === 'function') {
            props.onChange(e, props.data, getStrippedContents(e));
        }
    }, [getStrippedContents, props]);

    const onKeyDown = useCallback<KeyboardEventHandler<HTMLDivElement>>(e => {
        if (e.key === 'Enter' && e.metaKey) {
            if (props.onEnter) {
                e.preventDefault();

                props.onEnter(getStrippedContents(e));
                if (props.resetOnEnter) setValue('');

                // Refocus on the element
                contentRef?.current.focus();
            }
        }
        else if (e.key === 'Escape') {
            if (props.onCancel) {
                e.preventDefault();

                setValue(props.value ?? '');
                props.onCancel(e);

                // Refocus on the element
                contentRef?.current.focus();
            }
        }
    }, [props, getStrippedContents, contentRef]);

    /**
     * Copied from the 'net.
     *
     * @param contentEditableElement
     */
    const setEndOfContentEditable = useCallback((contentEditableElement: Node) => {
        let range: Range, selection: Selection;
        if (document.createRange) {
            range = document.createRange(); // Create a range (a range is a like the selection but invisible)
            range.selectNodeContents(contentEditableElement); // Select the entire contents of the element with the range
            range.collapse(false); // Collapse the range to the end point. false means collapse to end rather than the start
            selection = window.getSelection(); // Get the selection object (allows you to change selection)
            selection.removeAllRanges(); // Remove any selections already made
            selection.addRange(range); // Make the range you have just created the visible selection
        }
    }, []);

    /**
     * When focused, select the text in the div.
     */
    const onFocus = useCallback(() => {
        if (contentRef?.current) setEndOfContentEditable(contentRef?.current);
    }, [contentRef, setEndOfContentEditable]);

    /**
     * Called when pasting data from an external source.
     * This will strip all the html, leaving just text with minimal formatting.
     *
     * @param {Event} e
     */
    const onPaste = useCallback<ClipboardEventHandler<HTMLDivElement>>(e => {
        // Cancel paste
        e.preventDefault();

        const plain = e.clipboardData.getData('text/plain');
        const html = e.clipboardData.getData('text/html')
        const text = stripHTML(html, []);

        // Insert text manually
        const toPaste = props.stripHtml ? plain : text || plain;

        let selection = window.getSelection();
        if (!selection.rangeCount) return;

        let range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(toPaste));

        // Move the caret to the end of the pasted text
        selection = window.getSelection();
        selection.collapseToEnd();

        // Call the parent's onChange
        const content = props.stripHtml ? text : html;
        if (props.onChange) props.onChange(e, props.data, content || text || plain);
    }, [props, stripHTML]);

    const disabledClass = props.disabled ? 'disabled' : '';
    const textOnlyClass = props.stripHtml ? 'plain-text' : '';
    const className = props.className || '';

    const isEditable = (props.isEditable === null || props.isEditable === undefined) ? true : props.isEditable;

    return <div key={props.id}
                className={`text-field multiline-text component ${className} ${disabledClass} ${textOnlyClass}`.trim()}
                ref={contentRef}
                style={props.styles}
                contentEditable={isEditable}
                suppressContentEditableWarning={true}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                onInput={onChange}
                dangerouslySetInnerHTML={markup}
                onFocus={onFocus}
                onPaste={props.stripHtml ? onPaste : null}
                data-private={props.encrypted}
                data-placeholder={props.placeholder}
    />;
});

MultilineText.displayName = 'MultilineText';

export default MultilineText;
